import type { Block, WeatherValue } from '@kioskforge/shared';
import {
  EMPTY_BINDING,
  attributionText,
  formatDayLabel,
  formatLocalTime,
  formatTemp,
  loadValue,
  type WidgetDataBinding
} from './widgetData';
import { AttributionLine, StaleNotice, WidgetStateNotice } from './widgetState';

export type WeatherWidget = Extract<Block, { type: 'weather' }>;

/**
 * X3.3 — the weather widget's React half.
 *
 * Provider-agnostic (D0): it renders the normalised `WeatherValue` (A's
 * `packages/shared/src/values.ts`), never a provider shape. Two provider
 * registry entries producing the same value render identically, because there is
 * no provider code path here at all.
 *
 * Units are the *display* preference: the value stores Celsius and the widget
 * formats per `props.units` (docs/DATA_WIDGET_SPEC.md §2). `location_label` and
 * the list lengths are display prefs that never fabricate data: `days` clamps to
 * what the value actually carries (§3–4).
 */
export function WeatherWidgetView({
  block,
  binding = EMPTY_BINDING
}: {
  block: WeatherWidget;
  binding?: WidgetDataBinding;
}) {
  const state = loadValue<WeatherValue>(binding, 'weather', block.props.source.data_key);
  const timezone = binding.timezone;

  if (state.status !== 'ready' && state.status !== 'stale') {
    return <WidgetStateNotice status={state.status} reason={state.status === 'error' ? state.reason : undefined} />;
  }

  const value = state.value;
  const props = block.props;
  const label = props.location_label ?? value.location_label;
  // Display prefs never exceed the data (clamp, don't fabricate).
  const days = value.daily.slice(0, props.days);
  const hours = value.hourly.slice(0, 4);
  const attribution = attributionText(value, binding, 'weather');

  return (
    <div className={`player-weather player-weather-${props.layout}`} data-weather-layout={props.layout}>
      {state.status === 'stale' ? <StaleNotice lastGood={state.lastGood} timezone={timezone} /> : null}

      {label ? <p className="player-weather-place">{label}</p> : null}

      {props.layout === 'now' ? (
        <div className="player-weather-now">
          <span className="player-weather-temp mono">{formatTemp(value.now.temp_c, props.units)}</span>
          <span className="player-weather-condition">{value.now.condition}</span>
          <ul className="player-weather-facts mono">
            {value.now.feels_like_c !== undefined ? <li>feels {formatTemp(value.now.feels_like_c, props.units)}</li> : null}
            {props.show_rain && value.now.rain_chance !== undefined ? <li>rain {value.now.rain_chance}%</li> : null}
            {props.show_wind && value.now.wind_kph !== undefined ? <li>wind {value.now.wind_kph} kph</li> : null}
            {props.show_uv && value.now.uv !== undefined ? <li>UV {value.now.uv}</li> : null}
          </ul>
        </div>
      ) : null}

      {props.layout === 'hourly' ? (
        <ul className="player-weather-hourly mono">
          {hours.map((hour) => (
            <li key={hour.at}>
              <span>{formatLocalTime(hour.at, timezone)}</span>
              <span>{formatTemp(hour.temp_c, props.units)}</span>
              {props.show_rain && hour.rain_chance !== undefined ? <span>{hour.rain_chance}%</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {props.layout === 'daily' ? (
        <ul className="player-weather-daily mono">
          {days.map((day) => (
            <li key={day.date}>
              <span>{formatDayLabel(day.date)}</span>
              <span className="player-weather-range">
                {formatTemp(day.min_c, props.units)} / {formatTemp(day.max_c, props.units)}
              </span>
              {props.show_rain && day.rain_chance !== undefined ? <span>{day.rain_chance}%</span> : null}
              {props.show_uv && day.uv !== undefined ? <span>UV {day.uv}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {attribution ? <AttributionLine text={attribution} url={value.attribution?.url} /> : null}
    </div>
  );
}
